import { useEffect, useState } from "react";
import { Logo } from "@/components/atoms/Logo";
import { cn } from "@/lib/utils";

export type ProgressLoaderProps = {
  /** Messages shown in sequence, e.g. ["Fetching doctors…", "Almost done…"]. */
  messages: string[];
  /** Milliseconds each message stays on screen. */
  interval?: number;
  fullScreen?: boolean;
  className?: string;
};

/**
 * Branded, informative loader: breathing logo, indeterminate progress bar and
 * rotating status messages (announced politely to screen readers).
 * The last message stays visible until loading finishes.
 */
export function ProgressLoader({ messages, interval = 1400, fullScreen, className }: ProgressLoaderProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (messages.length < 2) return;
    const timer = window.setInterval(() => {
      setIndex((current) => Math.min(current + 1, messages.length - 1));
    }, interval);
    return () => window.clearInterval(timer);
  }, [messages, interval]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center gap-6 p-8 text-center",
        fullScreen && "brand-gradient fixed inset-0 z-50",
        className,
      )}
    >
      <span className="animate-breathe rounded-2xl">
        <Logo compact />
      </span>
      <div className="h-1 w-56 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className="h-full w-full origin-left animate-progress rounded-full bg-gradient-to-r from-primary to-accent" />
      </div>
      <p key={index} className="min-h-5 animate-fade-in-up text-sm font-medium text-muted-foreground">
        {messages[index]}
      </p>
    </div>
  );
}
